import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { BRAND } from "../config/brand";
import BrandMark from "../components/BrandMark";
import { getMyProfile, updateHelperProfile } from "../services/userService";
import { TAG_CATALOG, normalizePredefinedTag } from "../utils/predefinedTags";
import { toast } from "react-toastify";
import { deleteAccountAndData } from "../utils/deleteAccount";
import { deleteUser } from "firebase/auth";
import { auth } from "../firebase";
import { TIMEZONES, findTimezone, getDefaultTimezone } from "../utils/timezones";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { CONTINENTS, COUNTRIES_BY_CONTINENT, mapContinentValueToName } from "../utils/continentsData";

function Profile() {
  const { currentUser, currentUserRole } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    bio: "",
    location: "",
    company: "",
    timezone: getDefaultTimezone(),
    continent: "",
    country: "",
    city: "",
    skillsDescription: "",
    experienceLevel: "Intermediate",
    availability: "online",
    social: { github: "", linkedin: "", website: "" },
    selectedTags: [],
  });

  // Compute initials inline in render to avoid unused warnings

  // Helper function to format location object to string
  const formatLocation = (location) => {
    if (!location) return "";
    if (typeof location === "string") return location;
    if (typeof location === "object") {
      const parts = [];
      if (location.city) parts.push(location.city);
      if (location.country) parts.push(location.country);
      return parts.join(", ") || "";
    }
    return "";
  };

  // Helper function to get continent value from name
  const getContinentValue = (continentName) => {
    if (!continentName) return "";
    const found = CONTINENTS.find(c => c.label === continentName || c.value === continentName);
    return found ? found.value : "";
  };

  useEffect(() => {
    const load = async () => {
      try {
        const me = await getMyProfile();
        if (me) {
          setProfile(me);
          // Show every tag the helper already has, across all fields of
          // expertise — no longer locked to a single category.
          const location = me.location || {};
          setForm({
            name: me.name || currentUser?.displayName || "",
            bio: me.bio || "",
            location: formatLocation(me.location),
            company: me.company || "",
            timezone: findTimezone(location.timezone) || getDefaultTimezone(),
            continent: getContinentValue(location.continent) || "",
            country: location.country || "",
            city: location.city || "",
            skillsDescription: me.skillsDescription || "",
            experienceLevel: me.experienceLevel || "Intermediate",
            availability: me.availability || "online",
            social: me.social || { github: "", linkedin: "", website: "" },
            selectedTags: (me.skills || []).map(normalizePredefinedTag),
          });
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentUser]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSocialChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, social: { ...prev.social, [name]: value } }));
  };

  // No cap — helpers can select as many tags as apply, across any field.
  const toggleTag = (tag) => {
    setForm((prev) => {
      const t = normalizePredefinedTag(tag);
      const has = prev.selectedTags.includes(t);
      const next = has
        ? prev.selectedTags.filter((x) => x !== t)
        : [...prev.selectedTags, t];
      return { ...prev, selectedTags: next };
    });
  };

  const save = async () => {
    try {
      setLoading(true);
      // Handle location - preserve structured object format
      const selectedTimezone = findTimezone(form.timezone) || getDefaultTimezone();
      let locationToSave;
      
      if (currentUserRole === 'client') {
        // For clients, use the separate fields (continent, country, city, timezone)
        locationToSave = {
          city: form.city || "",
          country: form.country || "",
          continent: mapContinentValueToName(form.continent) || "",
          timezone: selectedTimezone,
        };
      } else if (currentUserRole === 'helper') {
        // For helpers, preserve object structure if exists
        if (profile?.location && typeof profile.location === 'object') {
          locationToSave = {
            ...profile.location,
            timezone: selectedTimezone,
          };
        } else {
          locationToSave = {
            city: "",
            country: "",
            continent: "",
            timezone: selectedTimezone,
          };
        }
      } else {
        locationToSave = {
          city: "",
          country: "",
          continent: "",
          timezone: selectedTimezone,
        };
      }
      
      const updateData = {
        name: form.name,
        bio: form.bio,
        location: locationToSave,
        company: form.company,
        social: form.social,
      };
      
      // Only include helper-specific fields if user is a helper
      if (currentUserRole === 'helper') {
        updateData.skillsDescription = form.skillsDescription;
        updateData.experienceLevel = form.experienceLevel;
        updateData.availability = form.availability;
        updateData.skills = form.selectedTags;
      }
      
      await updateHelperProfile(currentUser.uid, updateData);
      toast.success("Profile saved");
      setEditing(false);
      setProfile({ ...profile, ...form, location: locationToSave, skills: form.selectedTags });
    } catch (e) {
      console.error(e);
      toast.error("Failed to save profile");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!currentUser) return;
    try {
      setDeleting(true);
      await deleteAccountAndData(currentUser.uid, currentUserRole);
      await deleteUser(auth.currentUser);
      localStorage.removeItem("currentUser");
      localStorage.removeItem("userRole");
      toast.success("Account deleted successfully.");
      navigate("/login");
    } catch (error) {
      console.error("Account deletion failed", error);
      toast.error("Account deletion failed. Please contact support.");
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600" />
          <p className="text-sm text-slate-500">Loading profile…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-y-auto bg-slate-50 p-3 sm:p-6">
      <div className="mx-auto flex max-w-3xl items-center gap-2 pb-4">
        <Link to="/" className="flex items-center gap-2">
          <BrandMark size={32} />
          <span className="text-base font-semibold text-slate-900">{BRAND.name}</span>
        </Link>
      </div>
      <div className="max-w-3xl mx-auto bg-white p-4 sm:p-6 rounded-xl border border-slate-200 space-y-4 sm:space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-lg sm:text-2xl flex-shrink-0">
              {(() => {
                const fullName = (form.name || currentUser?.displayName || "User").trim();
                const parts = fullName.split(/\s+/);
                if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
                return fullName.substring(0, 2).toUpperCase();
              })()}
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 truncate">My Profile</h2>
              <p className="text-sm sm:text-base text-slate-500 truncate">{profile?.email || currentUser?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              className="rounded-lg border border-slate-300 px-3 sm:px-4 py-2 text-sm sm:text-base font-medium text-slate-700 hover:bg-slate-50 transition flex-1 sm:flex-none"
              onClick={() => navigate(currentUserRole === 'helper' ? '/dashboard-helper' : '/dashboard-client')}
            >
              Dashboard
            </button>
            {!editing ? (
              <button className="rounded-lg bg-blue-600 px-3 sm:px-4 py-2 text-sm sm:text-base font-medium text-white hover:bg-blue-700 transition flex-1 sm:flex-none" onClick={() => setEditing(true)}>
                Edit
              </button>
            ) : (
              <>
                <button className="rounded-lg border border-slate-300 px-3 sm:px-4 py-2 text-sm sm:text-base font-medium text-slate-700 hover:bg-slate-50 transition flex-1 sm:flex-none" onClick={() => setEditing(false)}>
                  Cancel
                </button>
                <button className="rounded-lg bg-blue-600 px-3 sm:px-4 py-2 text-sm sm:text-base font-medium text-white hover:bg-blue-700 transition flex-1 sm:flex-none" onClick={save}>
                  Save
                </button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Name</label>
            <input disabled={!editing} name="name" value={form.name} onChange={handleChange} className="w-full border border-slate-300 rounded-lg px-3 py-2 disabled:bg-slate-50 disabled:text-slate-500" />
          </div>
          <div className="md:col-span-2 space-y-2">
            <label className="block text-sm font-medium text-slate-700">Bio</label>
            <textarea disabled={!editing} name="bio" value={form.bio} onChange={handleChange} className="w-full border border-slate-300 rounded-lg px-3 py-2 h-24 disabled:bg-slate-50 disabled:text-slate-500" />
          </div>
          {/* Location fields for clients */}
          {currentUserRole === 'client' ? (
            <>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Continent <span className="text-rose-500">*</span></label>
                <div className="relative">
                  <select
                    disabled={!editing}
                    name="continent"
                    value={form.continent}
                    onChange={(e) => {
                      setForm(prev => ({ ...prev, continent: e.target.value, country: "", city: "" }));
                    }}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 pr-10 appearance-none bg-white cursor-pointer disabled:bg-slate-50 disabled:cursor-not-allowed"
                    required
                  >
                    <option value="">Select Continent</option>
                    {CONTINENTS.map((continent) => (
                      <option key={continent.value} value={continent.value}>
                        {continent.label}
                      </option>
                    ))}
                  </select>
                  {editing && (
                    <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                  )}
                </div>
              </div>
              {form.continent && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">Country <span className="text-rose-500">*</span></label>
                  <div className="relative">
                    <select
                      disabled={!editing}
                      name="country"
                      value={form.country}
                      onChange={(e) => {
                        setForm(prev => ({ ...prev, country: e.target.value, city: "" }));
                      }}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 pr-10 appearance-none bg-white cursor-pointer disabled:bg-slate-50 disabled:cursor-not-allowed"
                      required
                    >
                      <option value="">Select Country</option>
                      {COUNTRIES_BY_CONTINENT[form.continent]?.map((country) => (
                        <option key={country.value} value={country.value}>
                          {country.label}
                        </option>
                      ))}
                    </select>
                    {editing && (
                      <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                    )}
                  </div>
                </div>
              )}
              {form.country && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">City <span className="text-rose-500">*</span></label>
                  <input
                    disabled={!editing}
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 disabled:bg-slate-50"
                    required
                  />
                </div>
              )}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Timezone <span className="text-rose-500">*</span></label>
                <div className="relative">
                  <select
                    disabled={!editing}
                    name="timezone"
                    value={findTimezone(form.timezone)}
                    onChange={(e) => setForm(prev => ({ ...prev, timezone: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 pr-10 appearance-none bg-white cursor-pointer disabled:bg-slate-50 disabled:cursor-not-allowed"
                    required
                  >
                    {TIMEZONES.map((tz) => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </select>
                  {editing && (
                    <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                  )}
                </div>
                <p className="text-xs text-slate-500">Select your timezone for better matching</p>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Location</label>
                <input disabled={!editing} name="location" value={form.location} onChange={handleChange} className="w-full border border-slate-300 rounded-lg px-3 py-2" />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Timezone <span className="text-rose-500">*</span></label>
                <div className="relative">
                  <select
                    disabled={!editing}
                    name="timezone"
                    value={findTimezone(form.timezone)}
                    onChange={(e) => setForm(prev => ({ ...prev, timezone: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 pr-10 appearance-none bg-white cursor-pointer disabled:bg-slate-50 disabled:cursor-not-allowed"
                    required
                  >
                    {TIMEZONES.map((tz) => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </select>
                  {editing && (
                    <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                  )}
                </div>
                <p className="text-xs text-slate-500">Select your timezone for better matching</p>
              </div>
            </>
          )}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Company (clients)</label>
            <input disabled={!editing} name="company" value={form.company} onChange={handleChange} className="w-full border border-slate-300 rounded-lg px-3 py-2" />
          </div>
          {/* Only show helper-specific fields for helpers */}
          {currentUserRole === 'helper' && (
            <>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Experience Level</label>
                <select disabled={!editing} name="experienceLevel" value={form.experienceLevel} onChange={handleChange} className="w-full border border-slate-300 rounded-lg px-3 py-2">
                  <option value="Junior">Junior</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Expert">Expert</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Availability</label>
                <select disabled={!editing} name="availability" value={form.availability} onChange={handleChange} className="w-full border border-slate-300 rounded-lg px-3 py-2">
                  <option value="online">Available</option>
                  <option value="busy">Busy</option>
                  <option value="offline">Offline</option>
                </select>
              </div>
            </>
          )}
        </div>

        {/* Only show Expertise Hashtags for helpers */}
        {currentUserRole === 'helper' && (
          <div>
            <h3 className="font-semibold text-slate-900 mb-2">Expertise Hashtags</h3>
            <p className="text-xs text-slate-500 mb-3">
              Select your domain of expertise and the relevant tags.
            </p>
            <div className="space-y-4">
              {TAG_CATALOG.map((cat) => (
                <div key={cat.id}>
                  <p className="font-medium text-slate-800 mb-1">{cat.label}</p>
                  <div className="flex flex-wrap gap-2">
                    {cat.tags.map((tag) => {
                      const normalized = normalizePredefinedTag(tag);
                      const selected = form.selectedTags.includes(normalized);
                      return (
                        <button
                          type="button"
                          key={tag}
                          onClick={() => editing && toggleTag(tag)}
                          className={`px-3 py-1 rounded-full border text-sm ${
                            selected
                              ? "bg-blue-600 text-white border-blue-600"
                              : "bg-white text-slate-700 border-slate-300"
                          } ${editing ? "cursor-pointer" : "cursor-default"}`}
                        >
                          {tag}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">GitHub</label>
            <input disabled={!editing} name="github" value={form.social.github || ""} onChange={handleSocialChange} className="w-full border border-slate-300 rounded-lg px-3 py-2" />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">LinkedIn</label>
            <input disabled={!editing} name="linkedin" value={form.social.linkedin || ""} onChange={handleSocialChange} className="w-full border border-slate-300 rounded-lg px-3 py-2" />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Website</label>
            <input disabled={!editing} name="website" value={form.social.website || ""} onChange={handleSocialChange} className="w-full border border-slate-300 rounded-lg px-3 py-2" />
          </div>
        </div>

        <div className="pt-2 border-t border-slate-200">
          <button
            type="button"
            onClick={() => setShowDeleteModal(true)}
            className="w-full sm:w-auto bg-rose-600 hover:bg-rose-700 text-white font-semibold px-4 py-2 rounded-lg transition"
          >
            Delete My Account
          </button>
        </div>

        {showDeleteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4">
            <div className="w-full max-w-md rounded-xl bg-white shadow-xl p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-3">
                Are you sure you want to delete your SkillSyncs account? This action is permanent.
              </h3>
              <p className="text-sm text-slate-600 mb-6">
                All your data will be removed and you will be logged out.
              </p>
              <div className="flex flex-col sm:flex-row sm:justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 rounded-lg border border-slate-200 text-slate-800 hover:bg-slate-50 transition"
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  className="px-4 py-2 rounded-lg bg-rose-600 text-white font-semibold hover:bg-rose-700 transition disabled:opacity-70"
                  disabled={deleting}
                >
                  {deleting ? "Deleting..." : "Confirm Delete"}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default Profile;


